<div class="gfx-backdrop" (click)="onBackdropClick($event)">
  <div class="gfx-dialog">
    <!-- Header -->
    <header class="gfx-header">
      <h2 class="gfx-title">Graphics</h2>
      <button class="gfx-close" (click)="close()" aria-label="Close">&times;</button>
    </header>

    <!-- Presets -->
    <div class="gfx-presets">
      @for (preset of presets; track preset) {
        <button
          class="gfx-preset-btn"
          [class.active]="activePreset === preset"
          (click)="selectPreset(preset)"
        >
          {{ preset }}
        </button>
      }
      @if (!activePreset) {
        <span class="gfx-preset-custom">Custom</span>
      }
    </div>

    <!-- Settings Cards -->
    <div class="gfx-body">
      <!-- Resolution -->
      <section class="gfx-card">
        <h3 class="gfx-card-title">Resolution</h3>
        <label class="gfx-range">
          <span class="gfx-range-label">
            Render Scale
            <span class="gfx-range-value">{{ settings.renderScale }}x</span>
          </span>
          <input
            type="range"
            min="0.25"
            max="2"
            step="0.25"
            [value]="settings.renderScale"
            (input)="updateSetting('renderScale', +$any($event.target).value)"
          />
        </label>
      </section>

      <!-- Shadows -->
      <section class="gfx-card">
        <h3 class="gfx-card-title">Shadows</h3>

        <label class="gfx-inline">
          <span class="gfx-inline-label">Shadow Map</span>
          <select
            class="gfx-select"
            [value]="settings.shadowMapResolution"
            (change)="updateSetting('shadowMapResolution', +$any($event.target).value)"
          >
            <option value="512">512</option>
            <option value="1024">1024</option>
            <option value="2048">2048</option>
            <option value="4096">4096</option>
          </select>
        </label>

        <label class="gfx-inline">
          <span class="gfx-inline-label">Point Light</span>
          <select
            class="gfx-select"
            [value]="settings.pointShadowMapResolution"
            (change)="updateSetting('pointShadowMapResolution', +$any($event.target).value)"
          >
            <option value="256">256</option>
            <option value="512">512</option>
            <option value="1024">1024</option>
            <option value="2048">2048</option>
          </select>
        </label>

        <label class="gfx-range">
          <span class="gfx-range-label">
            Shadow Budget
            <span class="gfx-range-value">{{ settings.shadowBudget }}</span>
          </span>
          <input
            type="range"
            min="0"
            max="8"
            step="1"
            [value]="settings.shadowBudget"
            (input)="updateSetting('shadowBudget', +$any($event.target).value)"
          />
        </label>
      </section>

      <!-- Post-Processing (full width) -->
      <section class="gfx-card gfx-card-wide">
        <h3 class="gfx-card-title">Post-Processing</h3>
        <div class="gfx-pp-grid">
          <!-- Left column -->
          <div class="gfx-pp-col">
            <label class="gfx-checkbox">
              <input
                type="checkbox"
                [checked]="settings.ssaoEnabled"
                (change)="updateSetting('ssaoEnabled', $any($event.target).checked)"
              />
              <span>SSAO</span>
            </label>

            <label class="gfx-inline" [class.disabled]="!settings.ssaoEnabled">
              <span class="gfx-inline-label">Resolution</span>
              <select
                class="gfx-select"
                [disabled]="!settings.ssaoEnabled"
                [value]="settings.ssaoResolutionDivisor"
                (change)="updateSetting('ssaoResolutionDivisor', +$any($event.target).value)"
              >
                <option value="1">Full</option>
                <option value="2">Half</option>
                <option value="4">Quarter</option>
              </select>
            </label>

            <label class="gfx-checkbox">
              <input
                type="checkbox"
                [checked]="settings.godRaysEnabled"
                (change)="updateSetting('godRaysEnabled', $any($event.target).checked)"
              />
              <span>God Rays</span>
            </label>

            <label class="gfx-inline" [class.disabled]="!settings.godRaysEnabled">
              <span class="gfx-inline-label">Resolution</span>
              <select
                class="gfx-select"
                [disabled]="!settings.godRaysEnabled"
                [value]="settings.godRaysResolutionDivisor"
                (change)="updateSetting('godRaysResolutionDivisor', +$any($event.target).value)"
              >
                <option value="2">Half</option>
                <option value="4">Quarter</option>
                <option value="8">Eighth</option>
              </select>
            </label>

            <label class="gfx-checkbox">
              <input
                type="checkbox"
                [checked]="settings.volumetricEnabled"
                (change)="updateSetting('volumetricEnabled', $any($event.target).checked)"
              />
              <span>Volumetrics</span>
            </label>

            <label class="gfx-inline" [class.disabled]="!settings.volumetricEnabled">
              <span class="gfx-inline-label">Quality</span>
              <select
                class="gfx-select"
                [disabled]="!settings.volumetricEnabled"
                [value]="settings.volumetricQuality"
                (change)="updateSetting('volumetricQuality', $any($event.target).value)"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </label>

            <label class="gfx-checkbox">
              <input
                type="checkbox"
                [checked]="settings.aerialPerspectiveEnabled"
                (change)="updateSetting('aerialPerspectiveEnabled', $any($event.target).checked)"
              />
              <span>Aerial Perspective</span>
            </label>

            <label class="gfx-checkbox">
              <input
                type="checkbox"
                [checked]="settings.cloudVolumesEnabled"
                (change)="updateSetting('cloudVolumesEnabled', $any($event.target).checked)"
              />
              <span>Cloud Volumes</span>
            </label>
          </div>

          <!-- Right column -->
          <div class="gfx-pp-col">
            <label class="gfx-checkbox">
              <input
                type="checkbox"
                [checked]="settings.fxaaEnabled"
                (change)="updateSetting('fxaaEnabled', $any($event.target).checked)"
              />
              <span>FXAA</span>
            </label>

            <label class="gfx-checkbox">
              <input
                type="checkbox"
                [checked]="settings.taaEnabled"
                (change)="updateSetting('taaEnabled', $any($event.target).checked)"
              />
              <span>TAA</span>
            </label>

            <label class="gfx-checkbox">
              <input
                type="checkbox"
                [checked]="settings.bloomEnabled"
                (change)="updateSetting('bloomEnabled', $any($event.target).checked)"
              />
              <span>Bloom + Tonemap</span>
            </label>

            <label class="gfx-range" [class.disabled]="!settings.bloomEnabled">
              <span class="gfx-range-label">
                Threshold
                <span class="gfx-range-value">{{ settings.bloomThreshold }}</span>
              </span>
              <input
                type="range"
                min="0.1"
                max="3.0"
                step="0.1"
                [disabled]="!settings.bloomEnabled"
                [value]="settings.bloomThreshold"
                (input)="updateSetting('bloomThreshold', +$any($event.target).value)"
              />
            </label>

            <label class="gfx-range" [class.disabled]="!settings.bloomEnabled">
              <span class="gfx-range-label">
                Strength
                <span class="gfx-range-value">{{ settings.bloomStrength }}</span>
              </span>
              <input
                type="range"
                min="0.0"
                max="0.2"
                step="0.005"
                [disabled]="!settings.bloomEnabled"
                [value]="settings.bloomStrength"
                (input)="updateSetting('bloomStrength', +$any($event.target).value)"
              />
            </label>

            <label class="gfx-range" [class.disabled]="!settings.bloomEnabled">
              <span class="gfx-range-label">
                Exposure
                <span class="gfx-range-value">{{ settings.bloomExposure }}</span>
              </span>
              <input
                type="range"
                min="0.1"
                max="3.0"
                step="0.1"
                [disabled]="!settings.bloomEnabled"
                [value]="settings.bloomExposure"
                (input)="updateSetting('bloomExposure', +$any($event.target).value)"
              />
            </label>
          </div>
        </div>
      </section>

      <!-- Detail -->
      <section class="gfx-card">
        <h3 class="gfx-card-title">Detail</h3>

        <label class="gfx-range">
          <span class="gfx-range-label">
            LOD Distance
            <span class="gfx-range-value">{{ settings.lodDistanceScale }}x</span>
          </span>
          <input
            type="range"
            min="0.25"
            max="4"
            step="0.25"
            [value]="settings.lodDistanceScale"
            (input)="updateSetting('lodDistanceScale', +$any($event.target).value)"
          />
        </label>

        <label class="gfx-checkbox">
          <input
            type="checkbox"
            [checked]="settings.lodCrossfadeEnabled"
            (change)="updateSetting('lodCrossfadeEnabled', $any($event.target).checked)"
          />
          <span>LOD Crossfade</span>
        </label>
      </section>
    </div>
  </div>
</div>
